import { Button, Dropdown, MenuProps, Modal, Space, Spin, Table, Tag, Typography } from 'antd';
import { ColumnType } from 'antd/lib/table';
import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { Link, match as routerMatch } from 'react-router-dom';
import { Dispatch } from 'redux';
import RoleEnum from '~Api/Administrator/RoleEnum';
import ConditionalApprovalStatusEnum from '~Api/Application/ConditionalApprovalStatusEnum';
import IApplication from '~Api/Application/IApplication';
import IConditional from '~Api/Application/IConditional';
import {
    applicationConditionalApproveAction,
    applicationConditionalCancelSigningAction,
    applicationConditionalCheckSigningStatusAction,
    applicationConditionalDeleteAction,
    applicationConditionalSendAction,
    applicationConditionalSendForSigningAction,
    applicationConditionalsListAction,
    applicationGetAction,
} from '~Applications/actions';
import {
    applicationConditionalsSelector,
    applicationSelector,
} from '~Applications/selectors';
import IAuthUser from '~Auth/IAuthUser';
import { authCurrentUserSelector } from '~Auth/selectors';
import { IGlobalState } from '~reducer';
import { IDictionary } from '~utilities/IDictionary';
import ConditionalSendModal from './ConditionalSendModal';
import Layout from './Layout';
import IAdministrator from '~Api/Administrator/IAdministrator';
import { currentAdministratorSelector } from '~Administrators/selectors';
import ConditionalSigningStatusEnum from '~Api/Application/ConditionalSigningStatusEnum';
import ConditionalSigningVersionEnum from '~Api/Application/ConditionalSigningVersionEnum';
import WorkflowStatusEnum from '~Api/Application/WorkflowStatusEnum';
import { conditionalSigningStatusTagLabels } from '~Api/Application/conditionalSigningStatusTagLabels';
import PermissionsEnum from '~Api/Administrator/PermissionsEnum';
import enumToClassName from '~utilities/enumToClassName';

interface IState {
    sendConditionalUuid: string;
}

interface IMatch {
    applicationUuid: string;
}

interface IProps {
    match: routerMatch<IMatch>;
}

interface IPropsSelector {
    application: IApplication;
    conditionals: IDictionary<IConditional>;
    currentAdministrator: IAdministrator;
    currentUser: IAuthUser;
}

interface IPropsDispatch {
    applicationGet: () => void;
    approve: (payoutFigureUuid: string) => void;
    cancelSigning: (conditionalUuid: string) => void;
    checkSigningStatus: (conditionalUuid: string) => void;
    conditionalsList: () => void;
    delete: (payoutFigureUuid: string) => void;
    send: (conditionalUuid: string) => void;
    sendForSigning: (conditionalUuid: string) => void;
}

type Props = IProps & IPropsSelector & IPropsDispatch;

class Conditionals extends React.Component<Props> {
    public state: IState = {
        sendConditionalUuid: null,
    };

    constructor(props: Props) {
        super(props);

        this.onClickApprove = this.onClickApprove.bind(this);
        this.onClickCancelSigning = this.onClickCancelSigning.bind(this);
        this.onClickDelete = this.onClickDelete.bind(this);
        this.onClickSend = this.onClickSend.bind(this);
        this.onClickSendForSigning = this.onClickSendForSigning.bind(this);
        this.onClickSendTo = this.onClickSendTo.bind(this);
        this.onCancelSendTo = this.onCancelSendTo.bind(this);
    }

    public componentDidMount(): void {
        const { application } = this.props;

        if (!application) {
            this.props.applicationGet();
        }

        this.props.conditionalsList();
    }

    public render(): JSX.Element {
        const { application, conditionals, currentAdministrator, currentUser, match } = this.props;
        const { sendConditionalUuid } = this.state;

        if (!application || !conditionals) {
            return (
                <Layout applicationUuid={match.params.applicationUuid} section='conditional-approvals'>
                    <Typography.Title level={2}>Conditional Approvals</Typography.Title>
                    <Spin />
                </Layout>
            );
        }

        const isBdm: boolean = [ RoleEnum.BusinessDevelopmentManager, RoleEnum.InternalBusinessDevelopmentManager, RoleEnum.SeniorBusinessDevelopmentManager ].includes(currentAdministrator.role);
        const isWarehoused: boolean = application.workflowStatus === WorkflowStatusEnum.Warehoused;

        const currencyFormatter: Intl.NumberFormat = new Intl.NumberFormat('en-AU', {
            currency: 'AUD',
            style: 'currency',
        });

        const percentageFormatter: Intl.NumberFormat = Intl.NumberFormat('en-AU', {
            maximumFractionDigits: 2,
            minimumFractionDigits: 0,
            style: 'percent',
        });

        const columns: ColumnType<IConditional>[] = [
            {
                dataIndex: 'createdTime',
                defaultSortOrder: 'descend',
                render: (createdTime: string) => dayjs(createdTime).format('DD/MM/YYYY HH:mm'),
                sorter: (a: IConditional, b: IConditional) => dayjs(a.createdTime) > dayjs(b.createdTime) ? 1 : -1,
                title: 'Created',
                width: '15%',
            },
            {
                dataIndex: 'code',
                render: (code: string, record: IConditional) => {
                    const tag: JSX.Element = record.signingStatus && (
                        <Tag className={enumToClassName(record.signingStatus)}>
                            {conditionalSigningStatusTagLabels[record.signingStatus]}
                        </Tag>
                    );

                    return (
                        <Space>
                            {code}
                            {tag}
                        </Space>
                    );
                },
                title: 'Code',
                width: '20%',
            },
            {
                dataIndex: 'loanAmount',
                render: (loanAmount: number) => currencyFormatter.format(loanAmount),
                title: 'Loan Amount',
                width: '15%',
            },
            {
                dataIndex: 'termMonths',
                render: (termMonths: number) => `${termMonths} months`,
                title: 'Term',
                width: '15%',
            },
            {
                dataIndex: 'interestRate',
                render: (interestRate: number) => percentageFormatter.format(interestRate / 100),
                title: 'Interest Rate',
                width: '15%',
            },
            {
                render: (conditional: IConditional) => {
                    const onClickApprove: () => void = () => this.onClickApprove(conditional.uuid);
                    const onClickCancelSigning: () => void = () => this.onClickCancelSigning(conditional.uuid);
                    const onClickCheckSigningStatus: () => void = () => this.props.checkSigningStatus(conditional.uuid);
                    const onClickDelete: () => void = () => this.onClickDelete(conditional.uuid);
                    const onClickSend: () => void = () => this.onClickSend(conditional.uuid);
                    const onClickSendForSigning: () => void = () => this.onClickSendForSigning(conditional.uuid);
                    const onClickSendTo: () => void = () => this.onClickSendTo(conditional.uuid);

                    const applicationHasBroker: boolean = !!application.deal.broker || application.deal.isBroker;
                    const applicationHasWhitelabelBroker: boolean = !!application.deal.broker && !!application.deal.broker.whitelabelType;
                    const isDraft: boolean = conditional.approvalStatus === ConditionalApprovalStatusEnum.Draft;
                    const isSigned: boolean = conditional.signingStatus == ConditionalSigningStatusEnum.Signed;
                    const isSigning: boolean = conditional.signingStatus === ConditionalSigningStatusEnum.AwaitingSignature;
                    const isSigningVersion1: boolean = conditional.signingVersion === ConditionalSigningVersionEnum.Version1;
                    const isUnsignedOrCancelled: boolean = conditional.signingStatus === null || conditional.signingStatus === ConditionalSigningStatusEnum.Cancelled;
                    const userHasApprovePermission: boolean = currentUser.permissions.includes(PermissionsEnum.ApplicationConditionalApprove);

                    const menu: MenuProps = {
                        items: [],
                    };

                    const secondaryMenu: MenuProps = {
                        items: [],
                    };

                    if (!isDraft && !isSigned && !isSigningVersion1 && !applicationHasWhitelabelBroker) {
                        menu.items.push({
                            key: 'send',
                            label: `Send to ${applicationHasBroker ? 'Broker' : 'Borrower'}`,
                            onClick: onClickSend,
                        });
                    }

                    if (isDraft) {
                        if (userHasApprovePermission) {
                            menu.items.push({
                                key: 'edit',
                                label: <Link to={`/applications/${application.uuid}/conditional-approvals/${conditional.uuid}/edit`}>Edit</Link>,
                            });
                        }

                        menu.items.push({
                            danger: true,
                            key: 'delete',
                            label: 'Delete',
                            onClick: onClickDelete,
                        });
                    } else {
                        menu.items.push({
                            key: 'send-to',
                            label: 'Send To',
                            onClick: onClickSendTo,
                        });
                    }

                    if (isSigning) {
                        menu.items.push({
                            danger: true,
                            key: 'cancel-signing',
                            label: 'Cancel Signing',
                            onClick: onClickCancelSigning,
                        });
                    } else if (isSigned) {
                        secondaryMenu.items.push({
                            key: 'view-unsigned-pdf',
                            label: <a target='_blank' href={`${process.env.API_HOST}/application-conditionals/${conditional.uuid}/pdf`} rel='noreferrer'>View Unsigned PDF</a>,
                        });
                    }

                    const approveBtn: JSX.Element = isDraft && userHasApprovePermission && (
                        <Dropdown.Button
                            onClick={onClickApprove}
                            menu={menu}
                            type='primary'
                        >
                            Approve
                        </Dropdown.Button>
                    );

                    const checkSigningStatusBtn: JSX.Element = isSigning && (
                        <Dropdown.Button
                            onClick={onClickCheckSigningStatus}
                            menu={menu}
                            type='primary'
                        >
                            Check Signing Status
                        </Dropdown.Button>
                    );

                    const editBtn: JSX.Element = isDraft && !userHasApprovePermission && (
                        <Dropdown.Button
                            href={`/applications/${application.uuid}/conditional-approvals/${conditional.uuid}/edit`}
                            menu={menu}
                            type='primary'
                        >
                            Edit
                        </Dropdown.Button>
                    );

                    const sendForSigningBtn: JSX.Element = !isDraft && isUnsignedOrCancelled && !isSigningVersion1 && !applicationHasWhitelabelBroker && (
                        <Dropdown.Button
                            onClick={onClickSendForSigning}
                            menu={menu}
                            type='primary'
                        >
                            Send for Signing
                        </Dropdown.Button>
                    );

                    const sendToBtn: JSX.Element = (!isDraft && (isSigned || isSigningVersion1 || applicationHasWhitelabelBroker)) && (
                        <Dropdown.Button
                            onClick={onClickSend}
                            menu={menu}
                            type='primary'
                        >
                            Send to {applicationHasBroker ? 'Broker' : 'Borrower'}
                        </Dropdown.Button>
                    );

                    const viewPdfBtn: JSX.Element = !isSigned && (
                        <Button
                            href={`${process.env.API_HOST}/application-conditionals/${conditional.uuid}/pdf`}
                            target='_blank'
                        >
                            View PDF
                        </Button>
                    );

                    const viewSignedPdfBtn: JSX.Element = isSigned && (
                        <Dropdown.Button menu={secondaryMenu}>
                            <a
                                href={`${process.env.API_HOST}/application-conditionals/${conditional.uuid}/signed-pdf`}
                                target='_blank'
                                rel='noreferrer'
                            >
                                View Signed PDF
                            </a>
                        </Dropdown.Button>
                    );

                    return (
                        <Space>
                            {viewPdfBtn}
                            {editBtn}
                            {approveBtn}
                            {checkSigningStatusBtn}
                            {viewSignedPdfBtn}
                            {sendForSigningBtn}
                            {sendToBtn}
                            <ConditionalSendModal
                                isOpen={sendConditionalUuid === conditional.uuid}
                                onCancel={this.onCancelSendTo}
                                conditional={conditional}
                            />
                        </Space>
                    );
                },
                width: '10%',
            },
        ];

        return (
            <Layout applicationUuid={match.params.applicationUuid} section='conditional-approvals'>
                <Space className='issue'>
                    {!isBdm && !isWarehoused && <Link to={`/applications/${application.uuid}/conditional-approvals/add`}><Button className='issue'>Issue Conditional Approval</Button></Link>}
                </Space>
                <Typography.Title level={2}>Conditional Approvals</Typography.Title>
                <Table
                    columns={columns}
                    dataSource={_.values(conditionals)}
                    pagination={false}
                    rowKey='uuid'
                    size='middle'
                />
            </Layout>
        );
    }

    private onClickApprove(conditionalUuid: string): void {
        const { conditionals } = this.props;

        Modal.confirm({
            content: `Are you sure you want to approve draft Conditional Approval ${conditionals[conditionalUuid].code}?`,
            okText: 'Approve',
            onOk: () => {
                this.props.approve(conditionalUuid);
            },
            title: 'Approve Conditional Approval',
        });
    }

    private onClickDelete(conditionalUuid: string): void {
        const { conditionals } = this.props;

        Modal.confirm({
            content: `Are you sure you want to delete draft Conditional Approval ${conditionals[conditionalUuid].code}?`,
            okText: 'Delete',
            onOk: () => {
                this.props.delete(conditionalUuid);
            },
            title: 'Delete Conditional Approval',
        });
    }

    private onClickSend(conditionalUuid: string): void {
        const { conditionals } = this.props;

        Modal.confirm({
            content: `Are you sure you want to send the Conditional Approval ${conditionals[conditionalUuid].code}?`,
            okText: 'Send',
            onOk: () => {
                this.props.send(conditionalUuid);
            },
            title: 'Send Conditional Approval',
        });
    }

    private onClickSendForSigning(conditionalUuid: string): void {
        const { conditionals } = this.props;

        Modal.confirm({
            content: `Are you sure you want to send the Conditional Approval ${conditionals[conditionalUuid].code} for signing?`,
            okText: 'Send',
            onOk: () => {
                this.props.sendForSigning(conditionalUuid);
            },
            title: 'Send For Signing',
        });
    }

    private onClickCancelSigning(conditionalUuid: string): void {
        const { conditionals } = this.props;

        Modal.confirm({
            cancelText: 'No',
            content: `Are you sure you want to cancel signing for the Conditional Approval ${conditionals[conditionalUuid].code}?`,
            okText: 'Yes',
            onOk: () => {
                this.props.cancelSigning(conditionalUuid);
            },
            title: 'Cancel Signing',
        });
    }

    private onClickSendTo(sendConditionalUuid: string): void {
        this.setState({
            sendConditionalUuid,
        });
    }

    private onCancelSendTo(): void {
        this.setState({
            sendConditionalUuid: null,
        });
    }
}

function mapStateToProps(state: IGlobalState, ownProps: IProps): IPropsSelector {
    return {
        application: applicationSelector(state, ownProps.match.params.applicationUuid),
        conditionals: applicationConditionalsSelector(state, ownProps.match.params.applicationUuid),
        currentAdministrator: currentAdministratorSelector(state),
        currentUser: authCurrentUserSelector(state),
    };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: IProps): IPropsDispatch {
    return {
        applicationGet: () => dispatch(applicationGetAction(ownProps.match.params.applicationUuid)),
        approve: (conditionalUuid: string) => dispatch(applicationConditionalApproveAction(conditionalUuid)),
        cancelSigning: (conditionalUuid: string) => dispatch(applicationConditionalCancelSigningAction(conditionalUuid)),
        checkSigningStatus: (conditionalUuid: string) => dispatch(applicationConditionalCheckSigningStatusAction(conditionalUuid)),
        conditionalsList: () => dispatch(applicationConditionalsListAction(ownProps.match.params.applicationUuid)),
        delete: (conditionalUuid: string) => dispatch(applicationConditionalDeleteAction(conditionalUuid)),
        send: (conditionalUuid: string) => dispatch(applicationConditionalSendAction(conditionalUuid)),
        sendForSigning: (conditionalUuid: string) => dispatch(applicationConditionalSendForSigningAction(conditionalUuid)),
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(Conditionals);
